import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalesService } from '@/services/sales.service';
import { Contact } from '@/models/contact.model';
import { Button, FormInput, FormSelect, FormTextarea, LoadingSpinner, Card } from '@/components/common';
import { User, Users, Mail, Phone, Briefcase, Building2, FileText, ArrowLeft, Save } from 'lucide-react';

export const ContactForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Contact>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    accountId: '',
    title: '',
    notes: '',
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => SalesService.getAccounts(),
  });

  const { data: contact, isLoading } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => id ? SalesService.getContact(id) : null,
    enabled: isEditing && !!id,
  });

  React.useEffect(() => {
    if (contact) {
      setFormData(contact);
    }
  }, [contact]);

  const mutation = useMutation({
    mutationFn: async (data: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (isEditing && id) {
        await SalesService.updateContact(id, data);
      } else {
        await SalesService.createContact(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      navigate('/sales');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync(formData as Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>);
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner text="Loading contact..." />
      </div>
    );
  }

  const accountOptions = [
    { value: '', label: 'No account' },
    ...accounts.map(account => ({
      value: account.id,
      label: account.companyName,
    })),
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/sales')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            {isEditing ? 'Edit Contact' : 'New Contact'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update contact information' : 'Add a new contact to your CRM'}
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="First Name"
              icon={User}
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Enter first name"
            />

            <FormInput
              label="Last Name"
              icon={User}
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Enter last name"
            />

            <FormInput
              label="Email"
              type="email"
              icon={Mail}
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@example.com"
            />

            <FormInput
              label="Phone"
              type="tel"
              icon={Phone}
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />

            <FormInput
              label="Title"
              icon={Briefcase}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., CEO, VP of Sales"
            />

            <FormSelect
              label="Account"
              icon={Building2}
              options={accountOptions}
              value={formData.accountId || ''}
              onChange={(e) => setFormData({ ...formData, accountId: e.target.value || undefined })}
            />
          </div>

          <FormTextarea
            label="Notes"
            icon={FileText}
            rows={4}
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Add any additional notes about this contact..."
          />

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/sales')}
              icon={ArrowLeft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Contact' : 'Create Contact'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

